/**
 * setHrWindow — slice the swim's HR series to a single set's time window and
 * roll up the set-level avg/peak BPM the detail sheet shows.
 *
 * WHY BPM is allowed here (and nowhere per-length): the committee's "HR is a
 * feeling word, never a number" rule was scoped to PER-LENGTH rows, where a
 * one-length sample lag makes a printed BPM misleading. At SET level that lag
 * washes out across many lengths, so set-level avg/peak BPM is explicitly
 * sanctioned by the product owner (2026-06-11 spec delta). Per-length rows stay
 * words-only — this module is the ONLY place a BPM number originates for a set.
 *
 * Window is half-open [startMs, endMs): a sample exactly at endMs belongs to the
 * next set, matching how setGroups carves time. A set whose window contains no
 * samples (while the swim has HR elsewhere) returns null stats — the caller
 * renders nothing, silently (per the empty-state rule).
 *
 * Pure module (no Svelte runtime), per lib convention.
 */

export interface HrPoint {
  ts: number;
  bpm: number;
}

export interface SetHrStats {
  /** Mean BPM across in-window samples (un-rounded; caller formats). */
  avg: number;
  /** Max BPM across in-window samples. */
  peak: number;
}

/**
 * Samples of `series` whose timestamp falls in the half-open window
 * [startMs, endMs). Assumes nothing about ordering; filters defensively.
 */
export function hrSeriesInWindow(
  series: readonly HrPoint[],
  startMs: number,
  endMs: number,
): HrPoint[] {
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) {
    return [];
  }
  return series.filter((p) => p.ts >= startMs && p.ts < endMs);
}

/**
 * Set-level avg + peak BPM over [startMs, endMs). Null when the window holds no
 * samples (no HR for this set) — the caller omits the sparkline AND the avg/peak
 * line, with no placeholder. Avg is the arithmetic mean of in-window BPM values.
 */
export function setHrStats(
  series: readonly HrPoint[],
  startMs: number,
  endMs: number,
): SetHrStats | null {
  const inWindow = hrSeriesInWindow(series, startMs, endMs);
  if (inWindow.length === 0) return null;
  let sum = 0;
  let peak = -Infinity;
  for (const p of inWindow) {
    sum += p.bpm;
    if (p.bpm > peak) peak = p.bpm;
  }
  return { avg: sum / inWindow.length, peak };
}
